import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSettingsSafe } from "@shared/contexts/SettingsContext";
import logger from "@shared/utils/logger";

export type UserRole = "neurologist" | "technologist" | "resident" | "admin";

export type FontSize = "small" | "medium" | "large" | "extra-large";
export type LineSpacing = "normal" | "relaxed" | "spacious";
export type ColorBlindMode =
  | "none"
  | "protanopia"
  | "deuteranopia"
  | "tritanopia";

export interface AccessibilityPreferences {
  fontSize: FontSize;
  reducedMotion: boolean;
  highContrast: boolean;
  colorBlindMode: ColorBlindMode;
  lineSpacing: LineSpacing;
  largeTouchTargets: boolean;
}

export interface UserPreferences {
  dashboardLayout: string;
  theme: "light" | "dark" | "high-contrast";
  eegDefaults: {
    montage: string;
    sensitivity: number;
    timeScale: number;
    filters: {
      highPass: number;
      lowPass: number;
      notch: boolean;
    };
  };
  shortcuts: Record<string, string>;
  notifications: {
    criticalAlerts: boolean;
    studyUpdates: boolean;
    systemMessages: boolean;
  };
  displayPreferences: {
    compactView: boolean;
    showPatientPhotos: boolean;
    defaultPageSize: number;
  };
  accessibility: AccessibilityPreferences;
}

interface UserContextType {
  role: UserRole;
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  resetPreferences: () => void;
}

const defaultPreferences: UserPreferences = {
  dashboardLayout: "default",
  theme: "light",
  eegDefaults: {
    montage: "longitudinal-bipolar",
    sensitivity: 7,
    timeScale: 10,
    filters: {
      highPass: 0.5,
      lowPass: 70,
      notch: true,
    },
  },
  shortcuts: {},
  notifications: {
    criticalAlerts: true,
    studyUpdates: true,
    systemMessages: false,
  },
  displayPreferences: {
    compactView: false,
    showPatientPhotos: true,
    defaultPageSize: 10,
  },
  accessibility: {
    fontSize: "medium",
    reducedMotion: false,
    highContrast: false,
    colorBlindMode: "none",
    lineSpacing: "normal",
    largeTouchTargets: false,
  },
};

const UserPreferencesContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [role, setRole] = useState<UserRole>("neurologist"); // In real app, get from auth
  const [preferences, setPreferences] =
    useState<UserPreferences>(defaultPreferences);
  const { settings, loading: settingsLoading } = useSettingsSafe();

  // Load preferences from database settings (priority) or localStorage (fallback) on mount
  useEffect(() => {
    // Wait for settings to load from database
    if (settingsLoading) return;

    try {
      // Use database settings as the primary source
      const dbPreferences: Partial<UserPreferences> = {
        eegDefaults: {
          montage:
            settings.default_montage_id ||
            defaultPreferences.eegDefaults.montage,
          sensitivity:
            settings.default_sensitivity ||
            defaultPreferences.eegDefaults.sensitivity,
          timeScale:
            settings.default_time_scale ||
            defaultPreferences.eegDefaults.timeScale,
          filters: defaultPreferences.eegDefaults.filters, // Filters not in DB yet
        },
        displayPreferences: {
          ...defaultPreferences.displayPreferences,
          compactView: settings.workspace_density === "compact",
          defaultPageSize:
            settings.items_per_page ||
            defaultPreferences.displayPreferences.defaultPageSize,
        },
      };

      // Try to load additional preferences from localStorage
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem("userPreferences");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            // Merge: DB settings take priority over localStorage
            setPreferences({
              ...defaultPreferences,
              ...parsed,
              eegDefaults: {
                ...defaultPreferences.eegDefaults,
                ...(parsed.eegDefaults || {}),
                ...dbPreferences.eegDefaults, // DB overrides localStorage
              },
              notifications: {
                ...defaultPreferences.notifications,
                ...(parsed.notifications || {}),
              },
              displayPreferences: {
                ...defaultPreferences.displayPreferences,
                ...(parsed.displayPreferences || {}),
                ...dbPreferences.displayPreferences, // DB overrides localStorage
              },
              accessibility: {
                ...defaultPreferences.accessibility,
                ...(parsed.accessibility || {}),
              },
            });
          } catch (error) {
            logger.error("Failed to parse stored preferences:", error);
            // Fallback to DB settings only
            setPreferences({
              ...defaultPreferences,
              ...dbPreferences,
            });
          }
        } else {
          // No localStorage, use DB settings
          setPreferences({
            ...defaultPreferences,
            ...dbPreferences,
          });
        }
      } else {
        // No localStorage available, use DB settings
        setPreferences({
          ...defaultPreferences,
          ...dbPreferences,
        });
      }
    } catch (e) {
      // localStorage may be unavailable in some test environments (jsdom flags)
      // Fail gracefully and continue with defaults.
      // eslint-disable-next-line no-console
      logger.warn("localStorage unavailable for UserPreferencesProvider", e);
    }
  }, [settings, settingsLoading]);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(
          "userPreferences",
          JSON.stringify(preferences),
        );
      }
    } catch (e) {
      // ignore write errors in environments without localStorage
    }
  }, [preferences]);

  useEffect(() => {
    setPreferences((prev) => {
      const nextHighContrast = Boolean(settings.high_contrast);
      const nextReducedMotion = Boolean(settings.reduce_motion);
      const nextColorBlind =
        settings.color_vision_mode === "protanopia" ||
        settings.color_vision_mode === "deuteranopia" ||
        settings.color_vision_mode === "tritanopia"
          ? settings.color_vision_mode
          : "none";
      const nextFontSize =
        settings.font_size === "xlarge" || settings.font_size === "extra-large"
          ? "extra-large"
          : settings.font_size === "small" ||
              settings.font_size === "large" ||
              settings.font_size === "medium"
            ? settings.font_size
            : prev.accessibility.fontSize;
      if (
        prev.accessibility.highContrast === nextHighContrast &&
        prev.accessibility.reducedMotion === nextReducedMotion &&
        prev.accessibility.colorBlindMode === nextColorBlind &&
        prev.accessibility.fontSize === nextFontSize
      ) {
        return prev;
      }
      return {
        ...prev,
        accessibility: {
          ...prev.accessibility,
          highContrast: nextHighContrast,
          reducedMotion: nextReducedMotion,
          colorBlindMode: nextColorBlind,
          fontSize: nextFontSize,
        },
      };
    });
  }, [
    settings.color_vision_mode,
    settings.font_size,
    settings.high_contrast,
    settings.reduce_motion,
  ]);

  // Apply accessibility preferences to the DOM
  useEffect(() => {
    if (typeof document === "undefined") return;
    const { accessibility } = preferences;
    const root = document.documentElement;
    const body = document.body;
    const workspaceDensity =
      settings.workspace_density === "comfortable" ||
      settings.workspace_density === "compact" ||
      settings.workspace_density === "cozy"
        ? settings.workspace_density
        : "cozy";

    root.setAttribute("data-line-spacing", accessibility.lineSpacing);
    root.setAttribute("data-workspace-density", workspaceDensity);
    root.setAttribute(
      "data-large-touch-targets",
      String(accessibility.largeTouchTargets),
    );

    body.classList.remove(
      "line-spacing-normal",
      "line-spacing-relaxed",
      "line-spacing-spacious",
      "large-touch-targets",
      "workspace-density-comfortable",
      "workspace-density-cozy",
      "workspace-density-compact",
    );
    body.classList.add(`line-spacing-${accessibility.lineSpacing}`);
    body.classList.add(`workspace-density-${workspaceDensity}`);
    if (accessibility.largeTouchTargets) {
      body.classList.add("large-touch-targets");
    }
  }, [
    preferences.accessibility?.lineSpacing,
    preferences.accessibility?.largeTouchTargets,
    settings.workspace_density,
  ]);

  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...updates,
      // Deep merge for nested objects
      eegDefaults: {
        ...prev.eegDefaults,
        ...(updates.eegDefaults || {}),
        filters: {
          ...prev.eegDefaults.filters,
          ...(updates.eegDefaults?.filters || {}),
        },
      },
      notifications: {
        ...prev.notifications,
        ...(updates.notifications || {}),
      },
      displayPreferences: {
        ...prev.displayPreferences,
        ...(updates.displayPreferences || {}),
      },
      accessibility: {
        ...prev.accessibility,
        ...(updates.accessibility || {}),
      },
    }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem("userPreferences");
      }
    } catch (e) {
      // ignore
    }
  };

  return (
    <UserPreferencesContext.Provider
      value={{ role, preferences, updatePreferences, resetPreferences }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error(
      "useUserPreferences must be used within UserPreferencesProvider",
    );
  }
  return context;
};

// Role-based dashboard layouts
export const getDashboardLayoutForRole = (role: UserRole): string[] => {
  const layouts: Record<UserRole, string[]> = {
    neurologist: [
      "critical-alerts",
      "pending-reviews",
      "recent-studies",
      "quick-actions",
      "analytics",
      "scheduled-reads",
    ],
    technologist: [
      "critical-alerts",
      "today-schedule",
      "active-recordings",
      "quick-actions",
      "quality-metrics",
      "equipment-status",
    ],
    resident: [
      "critical-alerts",
      "learning-cases",
      "recent-studies",
      "quick-actions",
      "resources",
      "mentorship",
    ],
    admin: [
      "system-health",
      "user-activity",
      "reports-queue",
      "quick-actions",
      "analytics",
      "settings",
    ],
  };

  return layouts[role] || layouts.neurologist;
};

export default UserPreferencesContext;
